import axios from "axios";
import store from "store";
import { setTokenExpired } from "../Redux/authSlice";
import { getCookie, removeCookie, setCookie } from "../lib/cookies";

class AuthService {
  constructor() {
    this.TOKEN_KEY = "at";
    this.REFRESH_TOKEN_KEY = "rt";
    this.CHANNEL_KEY = "oauth_channel";
    this.isRefreshing = false;
    this.failedQueue = [];
  }

  processQueue(error, token = null) {
    this.failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });

    this.failedQueue = [];
  }

  saveAuthData(data) {
    let accessTokenExpirationDate = null;
    if (data.expires_in) {
      try {
        const [datePart, timePart] = data.expires_in.split(" ");
        const [day, month, year] = datePart.split("/");
        const [hours, minutes, seconds] = timePart.split(":");
        const fullYear = year.length === 2 ? `20${year}` : year;

        accessTokenExpirationDate = new Date(
          parseInt(fullYear, 10),
          parseInt(month, 10) - 1,
          parseInt(day, 10),
          parseInt(hours, 10),
          parseInt(minutes, 10),
          parseInt(seconds || 0, 10)
        );
      } catch (error) {
        console.error("Erro ao processar expiração do token:", error);
      }
    }

    if (data.access_token) {
      setCookie(this.TOKEN_KEY, data.access_token, accessTokenExpirationDate ? { expiresAt: accessTokenExpirationDate } : {});
    }
    if (data.refresh_token) {
      setCookie(this.REFRESH_TOKEN_KEY, data.refresh_token);
    }
    if (data.channel) {
      setCookie(this.CHANNEL_KEY, data.channel);
    }
  }

  getAccessToken() {
    return getCookie(this.TOKEN_KEY);
  }

  getRefreshToken() {
    return getCookie(this.REFRESH_TOKEN_KEY);
  }

  isTokenExpired() {
    return !this.getAccessToken();
  }

  async getNewToken() {
    try {
      const token = getCookie("wlt");
      if (!token) {
        console.error("Token não encontrado, não é possível obter customer");
        store.dispatch(setTokenExpired(true));
        return null;
      }

      const API_URL_CREDENCIAIS = `${process.env.REACT_APP_API_AUTHORIZATION}/v1`;
      let channel = process.env.REACT_APP_DEFAULT_CHANNEL;
      
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        channel = payload?.channel ?? channel;
      } catch (err) {
        console.warn('Falha ao decodificar token para obter channel:', err);
      }

      const response = await axios.post(
        `${API_URL_CREDENCIAIS}/jwt/validate-channel`,
        { channel, token }
      );

      const data = response?.data?.access_token;
   
      if (!data || !data.access_token) {
        throw new Error("Token de autorização não recebido");
      }

      this.saveAuthData(data);
      
      console.log('Token OAuth obtido com sucesso, resetando estado e redirecionando para home...');
      const wasExpired = store.getState().authSlice.isTokenExpired;
      store.dispatch(setTokenExpired(false));
      
      if (typeof window !== 'undefined' && wasExpired) {
        setTimeout(() => {
          window.location.href = '/loja';
        }, 100);
      }
      
      return data.access_token;
    } catch (error) {
      console.error("Erro ao obter novo token:", error);
      throw error;
    }
  }
  
  async refreshToken() {
    const refreshToken = this.getRefreshToken();
    const clientId = process.env.REACT_APP_API_AUTHORIZATION_CLIENT_ID;

    if (!refreshToken) {
      console.warn("Refresh token não disponível, obtendo novo token...");
      return await this.getNewToken();
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_AUTHORIZATION}/v1/oauth/refresh`,
        {
          client_id: clientId,
          refresh_token: refreshToken,
        }
      );

      const authData = response?.data?.data;
      
      if (!authData || !authData.access_token) {
        throw new Error("Token de autorização não recebido no refresh");
      }

      this.saveAuthData(authData);
      
      const wasExpired = store.getState().authSlice.isTokenExpired;
      
      store.dispatch(setTokenExpired(false));
      
      if (typeof window !== 'undefined' && wasExpired) {
        setTimeout(() => {
          window.location.href = '/loja';
        }, 100);
      }
      
      return authData.access_token;
    } catch (error) {
      console.error("Erro ao renovar token, tentando obter novo token:", error);
      return await this.getNewToken();
    }
  }

  async ensureValidToken() {
    const currentToken = this.getAccessToken();

    if (!currentToken || this.isTokenExpired()) {
      if (this.isRefreshing) {
        return new Promise((resolve, reject) => {
          this.failedQueue.push({ resolve, reject });
        });
      }

      this.isRefreshing = true;

      try {
        const newToken = await this.refreshToken();
        this.processQueue(null, newToken);
        return newToken;
      } catch (error) {
        this.processQueue(error, null);
        throw error;
      } finally {
        this.isRefreshing = false;
      }
    }

    return currentToken;
  }

  clearAuthData() {
    removeCookie(this.TOKEN_KEY);
    removeCookie(this.REFRESH_TOKEN_KEY);
    removeCookie(this.CHANNEL_KEY);
  }
}

export default new AuthService();

